import type { RackVolume } from "./rackVolume";
import type { DivergenceRow, StructureOccupancy, StructureOccupancyNode } from "../api/rest";
import { aisleReconciliation } from "../pages/inventoryReport";

/**
 * ADR 193: per-aisle KPI badges on the /live map. Values come from the
 * `structure/occupancy` rollup — the SAME aggregate the report panels read — so
 * an on-map badge and a report figure can never disagree (design §3.6). Anchors
 * come from the warehouse's rack volumes (run-independent): the badge floats
 * over the centroid of the aisle's rack footprints, above its tallest rack.
 *
 * Pure (no three.js/React) so the join — structure aisle `code` ↔ volume
 * `aisleLabel`, both minted by the same ADR 188 address grammar — is unit-tested.
 */
export interface AisleBadge {
  /** Join/react key: the aisle NODE id when the volumes carry one (ADR 281),
   *  else the label — two same-code aisles under different zones stay two
   *  badges instead of one anchor floating between them. */
  key: string;
  /** Display code. */
  aisle: string;
  /** Warehouse-frame anchor (meters). World placement is (x, *, y) — the
   *  no-flip convention of every direct-world floor consumer (AlertBeamLayer).
   *  The centroid: where the badge sits before `anchorOnAisle` slides it. */
  x: number;
  y: number;
  /** The aisle's run, as a segment in the warehouse frame (ADR 214): the badge
   *  travels along it toward whatever the camera is looking at, instead of
   *  sitting at the centroid 80 m from an operator inspecting one end. */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Top of the aisle's tallest rack, meters — the badge floats above this. */
  topM: number;
  coveragePct: number | null;
  occupancyPct: number | null;
  /** ADR 292 — mean vertical fill of the aisle's MEASURED occupied bins, 0–100, and how many
   *  bins that is. Null/0 where the rack-height sensor never fired in the aisle.
   *  `verticalDerived` (ADR 293): the mean leans on a clear height the twin states rather
   *  than one the kit measured, so the badge qualifies it. */
  verticalFillPct: number | null;
  verticalMeasuredSpaces: number;
  verticalDerived: boolean;
  /** WMS agreement (accuracy) of the aisle's reconciliation rows, 0–100.
   *  Null with no WMS snapshot or no rows joined to the aisle (ADR 195). */
  agreementPct: number | null;
}

interface Anchor {
  /** Display code for the badge (the key may be an opaque node id). */
  label: string;
  x: number; y: number; topM: number;
  x0: number; y0: number; x1: number; y1: number;
}

/** One anchor per aisle — keyed by the volumes' `aisleNodeId` when present
 *  (ADR 281), by `aisleLabel` otherwise: mean of the footprint centroids, max
 *  rack top, and the aisle's run as a segment. Volumes with neither anchor
 *  nothing.
 *
 *  The run is the extent of the member centroids along their DOMINANT axis
 *  (ADR 214) — aisles are axis-aligned runs of racks, so the longer side of
 *  their bounding box is the aisle direction. A single-rack aisle degenerates
 *  to a zero-length segment, which `anchorOnAisle` returns unchanged. */
export function aisleAnchors(volumes: RackVolume[]): Map<string, Anchor> {
  const acc = new Map<string, {
    label: string;
    sx: number; sy: number; n: number; topM: number;
    minX: number; maxX: number; minY: number; maxY: number;
  }>();
  for (const v of volumes) {
    const key = v.aisleNodeId ?? v.aisleLabel;
    if (!key || v.footprint.length === 0) continue;
    let cx = 0, cy = 0;
    for (const [x, y] of v.footprint) { cx += x; cy += y; }
    cx /= v.footprint.length;
    cy /= v.footprint.length;
    const top = v.baseM + v.heightM;
    const a = acc.get(key);
    if (a) {
      a.sx += cx; a.sy += cy; a.n += 1;
      if (top > a.topM) a.topM = top;
      if (cx < a.minX) a.minX = cx;
      if (cx > a.maxX) a.maxX = cx;
      if (cy < a.minY) a.minY = cy;
      if (cy > a.maxY) a.maxY = cy;
    } else {
      acc.set(key, {
        label: v.aisleLabel ?? key,
        sx: cx, sy: cy, n: 1, topM: top,
        minX: cx, maxX: cx, minY: cy, maxY: cy,
      });
    }
  }
  const out = new Map<string, Anchor>();
  for (const [key, a] of acc) {
    const x = a.sx / a.n, y = a.sy / a.n;
    const alongX = a.maxX - a.minX >= a.maxY - a.minY;
    out.set(key, {
      label: a.label,
      x, y, topM: a.topM,
      x0: alongX ? a.minX : x, y0: alongX ? y : a.minY,
      x1: alongX ? a.maxX : x, y1: alongX ? y : a.maxY,
    });
  }
  return out;
}

/** Beyond this the look-at point is effectively the horizon — treat the view as
 *  having no focus and leave the badge at the aisle's centre. */
const MAX_LOOK_DISTANCE_M = 400;

/**
 * Where on the aisle to float the badge, given the camera (ADR 214): the point
 * of the aisle's run nearest to what the operator is looking at, clamped to the
 * aisle's own extent so a badge never leaves its aisle.
 *
 * "What they are looking at" is the view ray's intersection with the horizontal
 * plane the badges float on — the centre of the view, always in front of the
 * camera and on the floor being inspected. Projecting THAT onto the run is
 * well-behaved; the tempting alternative (closest point between the run and the
 * view ray) is not: it happily parks the badge right beside the camera, where
 * `distanceFactor` inflates it to a full-screen card that projects off-screen.
 *
 * Falls back to the aisle centroid — the pre-ADR-214 anchor — whenever the view
 * has no usable focus: looking at or above the horizon, or away from the plane.
 *
 * Pure — no three.js — so the geometry is unit-tested directly.
 */
export function anchorOnAisle(
  b: Pick<AisleBadge, "x" | "y" | "x0" | "y0" | "x1" | "y1">,
  camPos: readonly [number, number, number],
  camDir: readonly [number, number, number],
  heightM: number,
): [number, number, number] {
  const ux = b.x1 - b.x0, uz = b.y1 - b.y0;
  const len2 = ux * ux + uz * uz;
  if (len2 < 1e-9) return [b.x0, heightM, b.y0];

  const [ox, oy, oz] = camPos;
  const [vx, vy, vz] = camDir;
  // Where the view ray crosses the badge plane. |vy| ~ 0 means looking level:
  // no crossing in front of the camera, so there is nothing to aim at.
  const t = Math.abs(vy) < 1e-4 ? -1 : (heightM - oy) / vy;
  if (t <= 0 || t > MAX_LOOK_DISTANCE_M) return [b.x, heightM, b.y];

  const lookX = ox + t * vx, lookZ = oz + t * vz;
  // Project the look-at point onto the run, clamped to the aisle's extent.
  const s = Math.min(1, Math.max(0, ((lookX - b.x0) * ux + (lookZ - b.y0) * uz) / len2));
  return [b.x0 + s * ux, heightM, b.y0 + s * uz];
}

/** Aisle-node rollups from the structure/occupancy tree, on two keys (ADR 281):
 *  `byId` — one entry per aisle NODE, the join id-keyed anchors use; `byCode` —
 *  the legacy label key, where two aisles sharing a code SUM, kept only for
 *  volumes payloads that predate `aisleNodeId` (their same-code volumes still
 *  collapse onto one anchor, so the rollup must aggregate the same way). */
export interface AisleRollups {
  byId: Map<string, StructureOccupancy>;
  byCode: Map<string, StructureOccupancy>;
}

export function collectAisleOccupancy(nodes: StructureOccupancyNode[]): AisleRollups {
  const byId = new Map<string, StructureOccupancy>();
  const byCode = new Map<string, StructureOccupancy>();
  const walk = (n: StructureOccupancyNode) => {
    if (n.kind === "aisle" && n.code) {
      byId.set(n.id, n.occupancy);
      const prev = byCode.get(n.code);
      if (prev) {
        // Vertical fill is a MEAN, so merging re-weights by measured bins (ADR 292) — the
        // two aisles' means averaged flat would let a 4-bin sample outvote a 400-bin one.
        const measured = prev.verticalMeasuredSpaces + n.occupancy.verticalMeasuredSpaces;
        const fillSum = (prev.verticalFillPct ?? 0) * prev.verticalMeasuredSpaces
          + (n.occupancy.verticalFillPct ?? 0) * n.occupancy.verticalMeasuredSpaces;
        const derived = prev.verticalDerivedSpaces + n.occupancy.verticalDerivedSpaces;
        const merged: StructureOccupancy = {
          occupiableSpaces: prev.occupiableSpaces + n.occupancy.occupiableSpaces,
          occupiedSpaces:   prev.occupiedSpaces   + n.occupancy.occupiedSpaces,
          emptySpaces:      prev.emptySpaces      + n.occupancy.emptySpaces,
          notScannedSpaces: prev.notScannedSpaces + n.occupancy.notScannedSpaces,
          occupancyPct: null,
          verticalMeasuredSpaces: measured,
          verticalFillPct: measured > 0 ? fillSum / measured : null,
          verticalDerivedSpaces: derived,
        };
        merged.occupancyPct = merged.occupiableSpaces > 0
          ? (merged.occupiedSpaces / merged.occupiableSpaces) * 100
          : null;
        byCode.set(n.code, merged);
      } else {
        byCode.set(n.code, n.occupancy);
      }
    }
    n.children.forEach(walk);
  };
  nodes.forEach(walk);
  return { byId, byCode };
}

/** Scanned share of the aisle's occupiable spaces, 0–100. Null when nothing is
 *  occupiable — the backend-never-fakes-a-ratio rule (mirrors occupancyPct). */
export function coveragePctOf(o: StructureOccupancy): number | null {
  if (o.occupiableSpaces <= 0) return null;
  return ((o.occupiableSpaces - o.notScannedSpaces) / o.occupiableSpaces) * 100;
}

/** Coverage quality band → semantic token. Coverage IS a quality measure (did
 *  the run visit the aisle), unlike occupancy which is a neutral quantity. */
export function coverageBand(pct: number | null): "good" | "warn" | "bad" | "na" {
  if (pct === null) return "na";
  if (pct >= 90) return "good";
  if (pct >= 60) return "warn";
  return "bad";
}

/** Occupancy → viridis bucket 0–4 (`--color-heat-N`, the scene's existing
 *  occupancy ramp). Neutral colour scale: full is not "bad". */
export function heatBucket(pct: number | null): number | null {
  if (pct === null) return null;
  return Math.min(4, Math.max(0, Math.floor(pct / 20)));
}

/** Per-aisle WMS agreement — the ADR 137 aisle-recon `accuracyPct`, computed by
 *  the SAME `aisleReconciliation` the run page's table renders (the badge and
 *  the report can never disagree). Address→aisle joins via the volumes; the
 *  "__none__" out-of-master bucket has no anchor and drops. */
export function collectAisleAgreement(
  rows: DivergenceRow[], volumes: RackVolume[],
): Map<string, number> {
  if (rows.length === 0) return new Map();
  const byAddress = new Map<string, string>();
  // Same key the anchors use (node id first, ADR 281) so the accuracy lands on
  // the badge it belongs to, not on a same-code sibling.
  for (const v of volumes) {
    const key = v.aisleNodeId ?? v.aisleLabel;
    if (key) byAddress.set(v.address, key);
  }
  const out = new Map<string, number>();
  for (const a of aisleReconciliation(rows, byAddress)) {
    if (a.aisle !== "__none__") out.set(a.aisle, a.accuracyPct);
  }
  return out;
}

/** Join anchors with rollups. Anchor-less rollups (aisle with no bound rack
 *  volumes) have nowhere to sit and drop; rollup-less anchors show "—". */
export function buildAisleBadges(
  volumes: RackVolume[], nodes: StructureOccupancyNode[], divRows: DivergenceRow[] = [],
): AisleBadge[] {
  const anchors = aisleAnchors(volumes);
  const rollups = collectAisleOccupancy(nodes);
  const agreement = collectAisleAgreement(divRows, volumes);
  const out: AisleBadge[] = [];
  for (const [key, a] of anchors) {
    // Node-id keys hit byId; label keys (pre-ADR-281 payloads) fall back to
    // the summed byCode map. Node ids and codes never collide — one is a path
    // id, the other a grammar token.
    const o = rollups.byId.get(key) ?? rollups.byCode.get(key);
    out.push({
      key,
      aisle: a.label,
      x: a.x, y: a.y, topM: a.topM,
      x0: a.x0, y0: a.y0, x1: a.x1, y1: a.y1,
      coveragePct: o ? coveragePctOf(o) : null,
      occupancyPct: o ? o.occupancyPct : null,
      verticalFillPct: o ? o.verticalFillPct : null,
      verticalMeasuredSpaces: o ? o.verticalMeasuredSpaces : 0,
      verticalDerived: !!o && o.verticalDerivedSpaces > 0,
      agreementPct: agreement.get(key) ?? null,
    });
  }
  return out.sort((l, r) => l.aisle.localeCompare(r.aisle) || l.key.localeCompare(r.key));
}
